import { useEffect, useState, useRef } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { WidgetProps, ActivityFeedItem } from './types'
import { transactionService } from '@/services/transactionService'

interface ActivityFeedWidgetProps extends WidgetProps {
  maxItems?: number
  autoScroll?: boolean
  soundAlerts?: boolean
  alertThreshold?: number
}

const ActivityFeedWidget = ({
  config,
  maxItems = 50,
  autoScroll = true,
  soundAlerts = false,
  alertThreshold = 10000,
  onUpdate,
  onRemove,
}: ActivityFeedWidgetProps) => {
  const [items, setItems] = useState<ActivityFeedItem[]>([])
  const [paused, setPaused] = useState(false)
  const [loading, setLoading] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const lastSoundTime = useRef<number>(0)

  const fetchRecentTransactions = async () => {
    if (paused) return
    setLoading(true)
    try {
      const [deposits, withdrawals] = await Promise.all([
        transactionService.fetchDeposits({ page: 1, page_size: maxItems }).catch(() => ({ items: [], pagination: { total: 0 } })),
        transactionService.fetchWithdrawals({ page: 1, page_size: maxItems }).catch(() => ({ items: [], pagination: { total: 0 } })),
      ])

      const allItems: ActivityFeedItem[] = [
        ...deposits.items.map((d: any) => ({
          id: d.id,
          type: 'deposit' as const,
          msisdn: d.msisdn,
          amount: d.amount,
          status: (d.status || 'PENDING') as 'COMPLETED' | 'PENDING' | 'FAILED',
          timestamp: d.createdAt,
          channel: d.channel,
          paybill: d.paybill,
        })),
        ...withdrawals.items.map((w: any) => ({
          id: w.id,
          type: 'withdrawal' as const,
          msisdn: w.msisdn,
          amount: w.amount,
          status: (w.status || 'PENDING') as 'COMPLETED' | 'PENDING' | 'FAILED',
          timestamp: w.createdAt,
          channel: w.channel,
          paybill: w.paybill,
        })),
      ]
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, maxItems)

      setItems(allItems)

      // Play sound for high-value transactions
      if (soundAlerts && allItems.length > 0) {
        const highValue = allItems.filter((item) => item.amount >= alertThreshold)
        if (highValue.length > 0) {
          const now = Date.now()
          if (now - lastSoundTime.current > 5000) {
            // Play sound (you can add actual sound file)
            // new Audio('/notification.mp3').play().catch(() => {})
            lastSoundTime.current = now
          }
        }
      }

      // Auto-scroll
      if (autoScroll && listRef.current) {
        listRef.current.scrollTop = 0
      }
    } catch (error) {
      console.error('Failed to fetch activity feed', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRecentTransactions()
    const interval = setInterval(fetchRecentTransactions, 30000) // Refresh every 30 seconds
    return () => clearInterval(interval)
  }, [paused, maxItems])

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`
    return date.toLocaleDateString()
  }

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' => {
    if (status === 'COMPLETED') return 'success'
    if (status === 'FAILED') return 'error'
    return 'warning'
  }

  const handleExport = () => {
    const csv = [
      ['Type', 'MSISDN', 'Amount', 'Status', 'Channel', 'Paybill', 'Timestamp'].join(','),
      ...items.map((item) =>
        [
          item.type,
          item.msisdn,
          item.amount,
          item.status,
          item.channel || '',
          item.paybill || '',
          item.timestamp,
        ].join(',')
      ),
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `activity_feed_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Activity Feed</Typography>
            <Stack direction="row" spacing={1}>
              <Tooltip title={paused ? 'Resume' : 'Pause'}>
                <IconButton size="small" onClick={() => setPaused(!paused)}>
                  {paused ? <PlayArrowIcon /> : <PauseIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Export CSV">
                <IconButton size="small" onClick={handleExport}>
                  <FileDownloadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch size="small" checked={autoScroll} onChange={(e) => onUpdate?.({ settings: { ...config.settings, autoScroll: e.target.checked } })} />}
              label="Auto-scroll"
            />
            <FormControlLabel
              control={<Switch size="small" checked={soundAlerts} onChange={(e) => onUpdate?.({ settings: { ...config.settings, soundAlerts: e.target.checked } })} />}
              label="Sound alerts"
            />
          </Stack>
        </Box>

        <Box
          ref={listRef}
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            maxHeight: 720, // Increased to show ~12 rows (60px per row)
            minHeight: 720, // Ensure minimum height for 12 visible rows
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: '4px',
            },
          }}
        >
          {loading && items.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No recent activity
              </Typography>
            </Box>
          ) : (
            <List dense sx={{ p: 0 }}>
              {items.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          label={item.type === 'deposit' ? 'Deposit' : 'Withdrawal'}
                          size="small"
                          color={item.type === 'deposit' ? 'success' : 'warning'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(item.amount)}
                        </Typography>
                        <Chip
                          label={item.status}
                          size="small"
                          color={getStatusColor(item.status)}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                        <Typography variant="caption" color="text.secondary">
                          {item.msisdn}
                        </Typography>
                        {item.channel && (
                          <Typography variant="caption" color="text.secondary">
                            • {item.channel}
                          </Typography>
                        )}
                        {item.paybill && (
                          <Typography variant="caption" color="text.secondary">
                            • {item.paybill}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {formatTime(item.timestamp)}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default ActivityFeedWidget


